import { supabase } from '@/shared/config/supabase';
import { createFeatureLogger } from '@/shared/services/logger';
import type { Database } from '@/shared/types/database.types';
import { IDashboardRepository } from '../../domain/repositories/IDashboardRepository';
import { DashboardStats } from '../../domain/entities/DashboardStats';
import { Activity } from '../../domain/entities/Activity';
import { ActivityType } from '../../domain/value-objects/ActivityType';
import { Course } from '../../domain/entities/Course';
import { ScheduleEvent } from '../../domain/entities/ScheduleEvent';
import { Notification } from '../../domain/entities/Notification';
import { WelcomeVideo } from '../../domain/entities/WelcomeVideo';
import { WelcomeVideoMapper } from '../../application/mappers/WelcomeVideoMapper';
import { CreateWelcomeVideoInput, UpdateWelcomeVideoInput } from '../../application/dto/WelcomeVideoDTO';
import { PendingVideoRow } from '../../application/dto/PendingVideoDTO';
import { TeacherStatsDTO, PendingSubmissionDTO, TeacherCourseDTO, UpcomingClassDTO } from '../../application/dto';

type Tables = Database['public']['Tables'];
type Views = Database['public']['Views'];

// Row types for tables used in this repository
type UserRow = Tables['users']['Row'];
type EnrollmentRow = Tables['Enrollments']['Row'];
type CourseRow = Tables['Courses']['Row'];
type SubjectRow = Tables['Subjects']['Row'];
type LevelRow = Tables['Levels']['Row'];
type ClassScheduleRow = Tables['ClassSchedules']['Row'];
type LessonRow = Tables['Lessons']['Row'];
type LessonProgressRow = Tables['LessonProgress']['Row'];
type QuestionResponseRow = Tables['QuestionResponses']['Row'];
type QuestionRow = Tables['Questions']['Row'];
type WelcomeVideoViewRow = Tables['welcome_video_views']['Row'];
type NotificationViewRow = Views['notifications_with_read_status']['Row'];

// Join result types for complex Supabase queries
interface StudentEnrollmentResult {
  id: EnrollmentRow['id'];
  progress_status: EnrollmentRow['progress_status'];
  final_grade: EnrollmentRow['final_grade'];
  course: {
    id: CourseRow['id'];
    is_draft: CourseRow['is_draft'];
    teacher: { id: UserRow['id']; full_name: UserRow['full_name'] } | null;
    subject: { id: SubjectRow['id']; name: SubjectRow['name'] } | null;
    modality: CourseRow['modality'];
    schedule_config?: Record<string, unknown> | null;
  } | null;
}

interface CourseWithJoinsResult {
  id: CourseRow['id'];
  is_draft: CourseRow['is_draft'];
  modality: CourseRow['modality'];
  teacher: { id: UserRow['id']; full_name: UserRow['full_name'] } | null;
  subject: { id: SubjectRow['id']; name: SubjectRow['name'] } | null;
  schedule_config?: Record<string, unknown> | null;
}

interface EnrollmentWithStudentResult {
  final_grade: EnrollmentRow['final_grade'];
  student: { workspace_id: UserRow['workspace_id'] } | null;
}

interface EnrollmentWithProgressResult {
  progress_status: EnrollmentRow['progress_status'];
  student: { workspace_id: UserRow['workspace_id'] } | null;
}

interface EnrollmentWithLevelResult {
  course: {
    subject: {
      level: { id: LevelRow['id']; name: LevelRow['name']; sort_order: LevelRow['sort_order'] } | null;
    } | null;
  } | null;
}

interface EnrollmentWithGradeResult {
  final_grade: EnrollmentRow['final_grade'];
  course: {
    subject: { level_id: SubjectRow['level_id'] } | null;
  } | null;
}

interface EnrollmentWithSubjectResult {
  course: {
    subject_id: CourseRow['subject_id'];
    subject: { level_id: SubjectRow['level_id'] } | null;
  } | null;
}

interface EnrollmentWithCompletionResult {
  progress_status: EnrollmentRow['progress_status'];
  course: {
    subject_id: CourseRow['subject_id'];
    subject: { level_id: SubjectRow['level_id'] } | null;
  } | null;
}

interface ScheduleWithCourseResult {
  id: ClassScheduleRow['id'];
  start_hour: ClassScheduleRow['start_hour'];
  start_minute: ClassScheduleRow['start_minute'];
  duration_minutes: ClassScheduleRow['duration_minutes'];
  course: {
    subject: { name: SubjectRow['name'] } | null;
  } | null;
}

interface PendingEnrollmentResult {
  id: EnrollmentRow['id'];
  course: {
    id: CourseRow['id'];
    teacher: { id: UserRow['id']; full_name: UserRow['full_name'] } | null;
    subject: { id: SubjectRow['id']; name: SubjectRow['name'] } | null;
  } | null;
}

// Partial row types for select() results (only selected columns)
type LessonSelectResult = Pick<LessonRow, 'id' | 'title' | 'content_type' | 'content_body'>;

type LessonProgressSelectResult = Pick<LessonProgressRow, 'lesson_id' | 'completed' | 'completed_at'>;

interface QuestionResponseWithQuestion {
  id: QuestionResponseRow['id'];
  is_correct: QuestionResponseRow['is_correct'];
  question: Pick<QuestionRow, 'question_type'> | null;
}

interface EnrollmentWithCourseTeacherId {
  student_id: EnrollmentRow['student_id'];
  course: { teacher_id: CourseRow['teacher_id'] } | null;
}

interface TeacherCourseSelectResult {
  id: CourseRow['id'];
  modality: CourseRow['modality'];
  subject: {
    id: SubjectRow['id'];
    name: SubjectRow['name'];
    level: Pick<LevelRow, 'id' | 'name'> | null;
  } | null;
}

interface TeacherScheduleSelectResult {
  id: ClassScheduleRow['id'];
  day_of_week: ClassScheduleRow['day_of_week'];
  start_hour: ClassScheduleRow['start_hour'];
  start_minute: ClassScheduleRow['start_minute'];
  duration_minutes: ClassScheduleRow['duration_minutes'];
  location: string | null;
  course: {
    id: CourseRow['id'];
    modality: CourseRow['modality'];
    teacher_id: CourseRow['teacher_id'];
    subject: Pick<SubjectRow, 'name'> | null;
  } | null;
}

const logger = createFeatureLogger('Dashboard');

/**
 * Supabase Dashboard Repository
 * Implements dashboard data access using Supabase
 */
export class SupabaseDashboardRepository implements IDashboardRepository {
  /**
   * Get courses for the authenticated user
   * - For students: Fetches enrollments and related course data
   * - For teachers: Fetches courses where they are the instructor
   * - For directors: Fetches all courses in their workspace
   */
  async getCourses(): Promise<Course[]> {
    try {
      // Get current user's session
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        logger.warn('No authenticated user found');
        return [];
      }

      const userId = session.user.id;
      logger.debug('Fetching courses for user', { userId });

      // Get user role and workspace
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('role, workspace_id')
        .eq('id', userId)
        .single();

      if (userError) {
        logger.error('Error fetching user data', userError);
        throw new Error(`Failed to fetch user data: ${userError.message}`);
      }

      const userRole = userData?.role?.toLowerCase();
      const workspaceId = userData?.workspace_id;
      logger.debug('User role resolved', { userRole, workspaceId });

      // Route to appropriate method based on role
      if (userRole === 'director') {
        return this.getCoursesForDirector(workspaceId);
      } else if (userRole === 'teacher') {
        return this.getCoursesForTeacher(userId);
      } else {
        return this.getCoursesForStudent(userId);
      }
    } catch (error) {
      logger.error('Error in getCourses', error);
      throw error;
    }
  }

  /**
   * Get courses for a student (via enrollments)
   */
  private async getCoursesForStudent(userId: string): Promise<Course[]> {
    logger.debug('Fetching courses for student', { userId });

    // Fetch enrollments with related course data
    // Using Supabase's JOIN syntax to get all related data in one query
    const { data: enrollments, error } = await supabase
      .from('Enrollments')
      .select(`
        id,
        progress_status,
        final_grade,
        course:Courses (
          id,
          is_draft,
          teacher:users!Courses_teacher_id_fkey (
            id,
            full_name
          ),
          subject:Subjects (
            id,
            name
          ),
          modality
        )
      `)
      .eq('student_id', userId);

    if (error) {
      logger.error('Error fetching enrollments', error);
      throw new Error(`Failed to fetch courses: ${error.message}`);
    }

    if (!enrollments || enrollments.length === 0) {
      logger.debug('No enrollments found for student');
      return [];
    }

    // Filter out draft courses - students should not see courses in draft mode
    const publishedEnrollments = (enrollments as unknown as StudentEnrollmentResult[]).filter(
      (e) => e.course && e.course.is_draft !== true
    );

    logger.debug('Enrollments fetched', { total: enrollments.length, published: publishedEnrollments.length });

    if (publishedEnrollments.length === 0) {
      logger.debug('No published courses found for student (all in draft mode)');
      return [];
    }

    // Collect course IDs from published enrollments and fetch progress in a single batch RPC
    const courseIds = publishedEnrollments
      .filter((e): e is StudentEnrollmentResult & { course: NonNullable<StudentEnrollmentResult['course']> } => e.course !== null)
      .map((e) => e.course.id);

    logger.debug('Fetching batch progress for courses', { courseIds });
    const progressMap = await this.getBatchCourseProgress([userId], courseIds);

    const courses: Course[] = [];
    publishedEnrollments.forEach((enrollment: StudentEnrollmentResult, index: number) => {
      const courseData = enrollment.course;

      if (!courseData) {
        logger.warn('Enrollment missing course data', { enrollmentId: enrollment.id });
        return;
      }

      // Look up progress from the batch Map
      let progress = progressMap.get(`${userId}:${courseData.id}`) ?? 0;

      // HYBRID LOGIC: If progress is 0% and enrollment status is COMPLETED, treat as 100%
      if (progress === 0 && enrollment.progress_status === 'COMPLETED') {
        progress = 100;
        logger.debug('Course marked COMPLETED with 0% batch progress, showing 100%', { courseId: courseData.id });
      }

      courses.push(new Course({
        id: String(courseData.id),
        slug: String(courseData.id),
        title: courseData.subject?.name || 'Sin título',
        progress: progress,
        instructor: courseData.teacher?.full_name || 'Sin instructor',
        nextClass: this.getNextClassFromSchedule(courseData.schedule_config, courseData.modality),
        color: this.getColorForCourse(index),
      }));
    });

    logger.debug('Final courses for student', { count: courses.length });
    return courses;
  }

  /**
   * Get courses for a director (all courses in their workspace)
   */
  private async getCoursesForDirector(workspaceId: number): Promise<Course[]> {
    logger.debug('Fetching courses for director', { workspaceId });

    // Get the active academic cycle for the workspace
    const { data: activeCycle } = await supabase
      .from('AcademicCycles')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('status', true)
      .single();

    if (!activeCycle) {
      logger.debug('No active academic cycle found for workspace');
      return [];
    }

    // Fetch all courses in the active academic cycle
    const { data: courses, error } = await supabase
      .from('Courses')
      .select(`
        id,
        is_draft,
        modality,
        teacher:users!Courses_teacher_id_fkey (
          id,
          full_name
        ),
        subject:Subjects (
          id,
          name
        )
      `)
      .eq('academic_cycle_id', activeCycle.id);

    if (error) {
      logger.error('Error fetching director courses', error);
      throw new Error(`Failed to fetch courses: ${error.message}`);
    }

    if (!courses || courses.length === 0) {
      logger.debug('No courses found for workspace');
      return [];
    }

    logger.debug('Director courses fetched', { count: courses.length });

    // Calculate progress for each course (average of all students)
    const coursesWithProgress = await Promise.all(
      (courses as unknown as CourseWithJoinsResult[]).map(async (courseData, index: number) => {
        logger.debug('Processing director course', {
          courseId: courseData.id,
          courseName: courseData.subject?.name,
        });

        // Get average progress across all students in this course
        const progress = await this.getTeacherCourseProgress(courseData.id);
        logger.debug('Course average progress', { courseId: courseData.id, progress });

        const course = new Course({
          id: String(courseData.id),
          slug: String(courseData.id),
          title: courseData.subject?.name || 'Sin título',
          progress: progress,
          instructor: courseData.teacher?.full_name || 'Sin instructor',
          nextClass: this.getNextClassFromSchedule(courseData.schedule_config, courseData.modality),
          color: this.getColorForCourse(index),
          isDraft: courseData.is_draft ?? false,
        });

        logger.debug('Director course created', { courseId: course.id });
        return course;
      })
    );

    const finalCourses = coursesWithProgress.filter(Boolean) as Course[];
    logger.debug('Final courses for director', { count: finalCourses.length });
    return finalCourses;
  }

  /**
   * Get courses for a teacher (courses they teach)
   */
  private async getCoursesForTeacher(userId: string): Promise<Course[]> {
    logger.debug('Fetching courses for teacher', { userId });

    // Fetch courses where user is the teacher (including drafts - teachers can prepare content)
    const { data: courses, error } = await supabase
      .from('Courses')
      .select(`
        id,
        is_draft,
        modality,
        teacher:users!Courses_teacher_id_fkey (
          id,
          full_name
        ),
        subject:Subjects (
          id,
          name
        )
      `)
      .eq('teacher_id', userId);

    if (error) {
      logger.error('Error fetching teacher courses', error);
      throw new Error(`Failed to fetch courses: ${error.message}`);
    }

    if (!courses || courses.length === 0) {
      logger.debug('No courses found for teacher', { userId });
      logger.debug('Teacher is not assigned to any courses in the Courses table');
      return [];
    }

    logger.debug('Teacher courses fetched', { count: courses.length });

    // Calculate progress for each course (average of all students)
    const coursesWithProgress = await Promise.all(
      (courses as unknown as CourseWithJoinsResult[]).map(async (courseData, index: number) => {
        logger.debug('Processing teacher course', {
          courseId: courseData.id,
          courseName: courseData.subject?.name,
        });

        // Get average progress across all students in this course
        const progress = await this.getTeacherCourseProgress(courseData.id);
        logger.debug('Course average progress', { courseId: courseData.id, progress });

        const course = new Course({
          id: String(courseData.id),
          slug: String(courseData.id), // Use ID as slug until migration is run
          title: courseData.subject?.name || 'Sin título',
          progress: progress,
          instructor: courseData.teacher?.full_name || 'Sin instructor',
          nextClass: this.getNextClassFromSchedule(courseData.schedule_config, courseData.modality),
          color: this.getColorForCourse(index),
          isDraft: courseData.is_draft ?? false,
        });

        logger.debug('Teacher course created', { courseId: course.id });
        return course;
      })
    );

    const finalCourses = coursesWithProgress.filter(Boolean) as Course[];
    logger.debug('Final courses for teacher', { count: finalCourses.length });
    return finalCourses;
  }

  /**
   * Get average course progress for a teacher (across all enrolled students)
   */
  private async getTeacherCourseProgress(courseId: number): Promise<number> {
    try {
      logger.debug('Calculating teacher course progress', { courseId });

      // Get all enrollments for this course
      const { data: enrollments, error } = await supabase
        .from('Enrollments')
        .select('student_id')
        .eq('course_id', courseId);

      if (error) {
        logger.error('Error fetching enrollments for course', error);
        return 0;
      }

      if (!enrollments || enrollments.length === 0) {
        logger.debug('No enrollments found for course');
        return 0;
      }

      // Batch fetch progress for all students in this course
      const studentIds = enrollments.map((e) => e.student_id);
      const progressMap = await this.getBatchCourseProgress(studentIds, [courseId]);

      // Compute average from the batch Map
      let totalProgress = 0;
      for (const studentId of studentIds) {
        totalProgress += progressMap.get(`${studentId}:${courseId}`) ?? 0;
      }

      const averageProgress = totalProgress / studentIds.length;
      logger.debug('Average progress for course', { progress: Math.round(averageProgress) });

      return Math.round(averageProgress);
    } catch (error) {
      logger.error('Error in getTeacherCourseProgress', error);
      return 0;
    }
  }

  /**
   * Batch fetch course progress for multiple student-course pairs in a single RPC call.
   * Returns a Map keyed by "studentId:courseId" with progress percentage values.
   * Returns empty Map on empty inputs or RPC error (no exception thrown).
   */
  private async getBatchCourseProgress(
    studentIds: string[],
    courseIds: number[]
  ): Promise<Map<string, number>> {
    const progressMap = new Map<string, number>();

    if (studentIds.length === 0 || courseIds.length === 0) {
      return progressMap;
    }

    const { data, error } = await supabase.rpc('get_batch_course_progress', {
      p_student_ids: studentIds,
      p_course_ids: courseIds,
    });

    if (error) {
      logger.error('Error in getBatchCourseProgress', error);
      return progressMap;
    }

    if (data && Array.isArray(data)) {
      for (const row of data) {
        const key = `${row.student_id}:${row.course_id}`;
        progressMap.set(key, row.progress ?? 0);
      }
    }

    return progressMap;
  }

  /**
   * Format class modality type
   */
  private getNextClassFromSchedule(_scheduleConfig: Record<string, unknown> | null | undefined, modality: string | null): string {
    // Return modality type or default
    if (!modality) {
      return 'Por confirmar';
    }

    // Map database modality to display text
    switch (modality.toUpperCase()) {
      case 'ONSITE':
        return 'Presencial';
      case 'ONLINE':
        return 'Virtual';
      default:
        return 'Por confirmar';
    }
  }

  /**
   * Assign a color to each course (cycling through predefined colors)
   */
  private getColorForCourse(index: number): string {
    const colors = [
      '#6D5DFE', // Purple
      '#FF6B9D', // Pink
      '#4ECDC4', // Teal
      '#FFA500', // Orange
      '#9D8DFE', // Light purple
      '#FF8C94', // Coral
    ];

    return colors[index % colors.length];
  }

  // ============================================
  // TODO: Implement these methods with real data
  // For now, they return mock data
  // ============================================

  async getDashboardStats(role: string): Promise<DashboardStats> {
    try {
      // Get current user's session
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        logger.warn('No authenticated user found for dashboard stats');
        return this.getEmptyStats(role);
      }

      const userId = session.user.id;
      console.log('SupabaseDashboardRepository.getDashboardStats: Fetching stats for user:', userId, 'role:', role);

      // Director gets institution-wide stats
      if (role === 'director') {
        return this.getDirectorStats(userId);
      }

      // Student/Teacher gets individual stats
      // 1. Get active level (highest sort_order of enrolled courses)
      const activeLevel = await this.getActiveLevel(userId);
      console.log('SupabaseDashboardRepository.getDashboardStats: Active level:', activeLevel);

      if (!activeLevel) {
        logger.warn('No active level found for user');
        return this.getEmptyStats(role);
      }

      // 2. Get average grade for active level
      const avgGrade = await this.getAverageGrade(userId, activeLevel.id);
      console.log('SupabaseDashboardRepository.getDashboardStats: Average grade:', avgGrade);

      // 3. Get subjects count for active level
      const subjectsCount = await this.getSubjectsCount(userId, activeLevel.id);
      console.log('SupabaseDashboardRepository.getDashboardStats: Subjects count:', subjectsCount);

      // 4. Get completion rate for active level
      const completionRate = await this.getCompletionRate(userId, activeLevel.id);
      console.log('SupabaseDashboardRepository.getDashboardStats: Completion rate:', completionRate);

      return new DashboardStats({
        stat1: {
          icon: 'Award',
          label: 'Promedio General',
          value: avgGrade.toFixed(1),
          trend: avgGrade > 0 ? `Nivel ${activeLevel.name}` : 'Sin calificaciones',
          color: 'purple',
        },
        stat2: {
          icon: 'BookOpen',
          label: 'Materias Actuales',
          value: subjectsCount.toString(),
          trend: activeLevel.name,
          color: 'blue',
        },
        stat3: {
          icon: 'Target',
          label: 'Completado',
          value: `${completionRate}%`,
          trend: completionRate > 0 ? `${completionRate}% del nivel` : 'Iniciando',
          color: 'green',
        },
      });
    } catch (error) {
      logger.error('Error fetching dashboard stats', error);
      return this.getEmptyStats(role);
    }
  }

  /**
   * Get institution-wide stats for director
   */
  private async getDirectorStats(userId: string): Promise<DashboardStats> {
    try {
      // Get user's workspace_id and role
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('workspace_id, role')
        .eq('id', userId)
        .single();

      if (userError) {
        logger.error('Error fetching user data', userError);
        return this.getEmptyStats('director');
      }

      // ADMIN users don't have workspace-specific stats
      if (userData?.role === 'ADMIN' || !userData?.workspace_id) {
        return this.getEmptyStats('director');
      }

      const workspaceId = userData.workspace_id;

      // 1. Get institution-wide average grade
      const avgGrade = await this.getInstitutionAverageGrade(workspaceId);

      // 2. Get total active subjects count
      const subjectsCount = await this.getInstitutionSubjectsCount(workspaceId);

      // 3. Get overall completion rate
      const completionRate = await this.getInstitutionCompletionRate(workspaceId);

      // 4. Get total students enrolled
      const studentsCount = await this.getInstitutionStudentsCount(workspaceId);

      return new DashboardStats({
        stat1: {
          icon: 'Award',
          label: 'Promedio General',
          value: avgGrade.toFixed(1),
          trend: avgGrade > 0 ? 'Promedio institucional' : 'Sin calificaciones',
          color: 'purple',
        },
        stat2: {
          icon: 'BookOpen',
          label: 'Materias Activas',
          value: subjectsCount.toString(),
          trend: `${studentsCount} estudiantes`,
          color: 'blue',
        },
        stat3: {
          icon: 'Target',
          label: 'Completado',
          value: `${completionRate}%`,
          trend: completionRate > 0 ? 'Cursos finalizados' : 'En progreso',
          color: 'green',
        },
      });
    } catch (error) {
      logger.error('Error fetching director stats', error);
      return this.getEmptyStats('director');
    }
  }

  /**
   * Get institution-wide average grade across all students
   */
  private async getInstitutionAverageGrade(workspaceId: number): Promise<number> {
    try {
      // Get all enrollments with grades for students in this workspace
      const { data, error } = await supabase
        .from('Enrollments')
        .select(`
          final_grade,
          student:users!Enrollments_student_id_fkey (
            workspace_id
          )
        `)
        .not('final_grade', 'is', null);

      if (error) {
        logger.error('Error fetching institution average grade', error);
        return 0;
      }

      if (!data || data.length === 0) {
        return 0;
      }

      // Filter by workspace and calculate average
      const grades = (data as unknown as EnrollmentWithStudentResult[])
        .filter((enrollment) => enrollment.student?.workspace_id === workspaceId)
        .map((enrollment) => enrollment.final_grade)
        .filter((grade): grade is number => grade !== null && grade !== undefined);

      if (grades.length === 0) {
        return 0;
      }

      const sum = grades.reduce((acc: number, grade: number) => acc + grade, 0);
      return sum / grades.length;
    } catch (error) {
      logger.error('Error in getInstitutionAverageGrade', error);
      return 0;
    }
  }

  /**
   * Get total active subjects count in the workspace
   */
  private async getInstitutionSubjectsCount(workspaceId: number): Promise<number> {
    try {
      // Get subjects for levels in this workspace
      const { data, error } = await supabase
        .from('Subjects')
        .select(`
          id,
          level:Levels!inner (
            workspace_id
          )
        `)
        .eq('level.workspace_id', workspaceId);

      if (error) {
        logger.error('Error fetching institution subjects count', error);
        return 0;
      }

      return data?.length || 0;
    } catch (error) {
      logger.error('Error in getInstitutionSubjectsCount', error);
      return 0;
    }
  }

  /**
   * Get overall completion rate across all students in the institution
   */
  private async getInstitutionCompletionRate(workspaceId: number): Promise<number> {
    try {
      // Get all enrollments for students in this workspace
      const { data, error } = await supabase
        .from('Enrollments')
        .select(`
          progress_status,
          student:users!Enrollments_student_id_fkey (
            workspace_id
          )
        `);

      if (error) {
        logger.error('Error fetching institution completion rate', error);
        return 0;
      }

      if (!data || data.length === 0) {
        return 0;
      }

      // Filter by workspace
      const typedData = data as unknown as EnrollmentWithProgressResult[];
      const workspaceEnrollments = typedData.filter(
        (enrollment) => enrollment.student?.workspace_id === workspaceId
      );

      if (workspaceEnrollments.length === 0) {
        return 0;
      }

      // Count completed enrollments
      const completedCount = workspaceEnrollments.filter(
        (enrollment) => enrollment.progress_status === 'COMPLETED'
      ).length;

      // Calculate percentage
      const rate = (completedCount / workspaceEnrollments.length) * 100;
      return Math.round(rate);
    } catch (error) {
      logger.error('Error in getInstitutionCompletionRate', error);
      return 0;
    }
  }

  /**
   * Get total students count enrolled in the institution
   */
  private async getInstitutionStudentsCount(workspaceId: number): Promise<number> {
    try {
      // Get unique students with enrollments in this workspace
      const { data, error } = await supabase
        .from('users')
        .select('id')
        .eq('workspace_id', workspaceId)
        .eq('role', 'STUDENT');

      if (error) {
        logger.error('Error fetching institution students count', error);
        return 0;
      }

      return data?.length || 0;
    } catch (error) {
      logger.error('Error in getInstitutionStudentsCount', error);
      return 0;
    }
  }

  /**
   * Get empty/default stats based on role
   */
  private getEmptyStats(role?: string): DashboardStats {
    if (role === 'director') {
      return new DashboardStats({
        stat1: {
          icon: 'Award',
          label: 'Promedio General',
          value: '0.0',
          trend: 'Sin calificaciones',
          color: 'purple',
        },
        stat2: {
          icon: 'BookOpen',
          label: 'Materias Activas',
          value: '0',
          trend: '0 estudiantes',
          color: 'blue',
        },
        stat3: {
          icon: 'Target',
          label: 'Completado',
          value: '0%',
          trend: 'Sin cursos',
          color: 'green',
        },
      });
    }

    return new DashboardStats({
      stat1: {
        icon: 'Award',
        label: 'Promedio General',
        value: '0.0',
        trend: 'Sin calificaciones',
        color: 'purple',
      },
      stat2: {
        icon: 'BookOpen',
        label: 'Materias Actuales',
        value: '0',
        trend: 'Sin inscripciones',
        color: 'blue',
      },
      stat3: {
        icon: 'Target',
        label: 'Completado',
        value: '0%',
        trend: 'Iniciando',
        color: 'green',
      },
    });
  }

  /**
   * Get active level for student (highest sort_order of enrolled courses)
   */
  private async getActiveLevel(studentId: string): Promise<{ id: number; name: string } | null> {
    try {
      const { data, error } = await supabase
        .from('Enrollments')
        .select(`
          course:Courses (
            subject:Subjects (
              level:Levels (
                id,
                name,
                sort_order
              )
            )
          )
        `)
        .eq('student_id', studentId)
        .eq('progress_status', 'ENROLLED');

      if (error) {
        logger.error('Error fetching active level', error);
        return null;
      }

      if (!data || data.length === 0) {
        return null;
      }

      // Extract all levels and find the one with highest sort_order
      type LevelInfo = { id: LevelRow['id']; name: LevelRow['name']; sort_order: LevelRow['sort_order'] };
      const levels = (data as unknown as EnrollmentWithLevelResult[])
        .map((enrollment) => enrollment.course?.subject?.level)
        .filter((level): level is LevelInfo => level !== null && level !== undefined);

      if (levels.length === 0) {
        return null;
      }

      // Find level with highest sort_order
      const activeLevel = levels.reduce((max: LevelInfo, level: LevelInfo) => {
        return ((level.sort_order ?? 0) > (max.sort_order ?? 0)) ? level : max;
      });

      return { id: activeLevel.id, name: activeLevel.name ?? '' };
    } catch (error) {
      logger.error('Error in getActiveLevel', error);
      return null;
    }
  }

  /**
   * Get average grade for student in a specific level
   */
  private async getAverageGrade(studentId: string, levelId: number): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('Enrollments')
        .select(`
          final_grade,
          course:Courses (
            subject:Subjects (
              level_id
            )
          )
        `)
        .eq('student_id', studentId)
        .not('final_grade', 'is', null);

      if (error) {
        logger.error('Error fetching average grade', error);
        return 0;
      }

      if (!data || data.length === 0) {
        return 0;
      }

      // Filter enrollments for this level
      const levelGrades = (data as unknown as EnrollmentWithGradeResult[])
        .filter((enrollment) => enrollment.course?.subject?.level_id === levelId)
        .map((enrollment) => enrollment.final_grade)
        .filter((grade): grade is number => grade !== null && grade !== undefined);

      if (levelGrades.length === 0) {
        return 0;
      }

      // Calculate average
      const sum = levelGrades.reduce((acc: number, grade: number) => acc + grade, 0);
      return sum / levelGrades.length;
    } catch (error) {
      logger.error('Error in getAverageGrade', error);
      return 0;
    }
  }

  /**
   * Get count of subjects for student in a specific level
   */
  private async getSubjectsCount(studentId: string, levelId: number): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('Enrollments')
        .select(`
          course:Courses (
            subject_id,
            subject:Subjects (
              level_id
            )
          )
        `)
        .eq('student_id', studentId)
        .eq('progress_status', 'ENROLLED');

      if (error) {
        logger.error('Error fetching subjects count', error);
        return 0;
      }

      if (!data || data.length === 0) {
        return 0;
      }

      // Get unique subject IDs for this level
      const typedSubjectData = data as unknown as EnrollmentWithSubjectResult[];
      const uniqueSubjects = new Set(
        typedSubjectData
          .filter((enrollment) => enrollment.course?.subject?.level_id === levelId)
          .map((enrollment) => enrollment.course?.subject_id)
          .filter(Boolean)
      );

      return uniqueSubjects.size;
    } catch (error) {
      logger.error('Error in getSubjectsCount', error);
      return 0;
    }
  }

  /**
   * Get completion rate for student in a specific level
   */
  private async getCompletionRate(studentId: string, levelId: number): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('Enrollments')
        .select(`
          progress_status,
          course:Courses (
            subject_id,
            subject:Subjects (
              level_id
            )
          )
        `)
        .eq('student_id', studentId);

      if (error) {
        logger.error('Error fetching completion rate', error);
        return 0;
      }

      if (!data || data.length === 0) {
        return 0;
      }

      // Filter enrollments for this level
      const typedCompletionData = data as unknown as EnrollmentWithCompletionResult[];
      const levelEnrollments = typedCompletionData.filter(
        (enrollment) => enrollment.course?.subject?.level_id === levelId
      );

      if (levelEnrollments.length === 0) {
        return 0;
      }

      // Get unique subjects (total and completed)
      const allSubjects = new Set(
        levelEnrollments
          .map((e) => e.course?.subject_id)
          .filter(Boolean)
      );

      const completedSubjects = new Set(
        levelEnrollments
          .filter((e) => e.progress_status === 'COMPLETED')
          .map((e) => e.course?.subject_id)
          .filter(Boolean)
      );

      if (allSubjects.size === 0) {
        return 0;
      }

      // Calculate percentage
      const rate = (completedSubjects.size / allSubjects.size) * 100;
      return Math.round(rate);
    } catch (error) {
      logger.error('Error in getCompletionRate', error);
      return 0;
    }
  }

  async getRecentActivities(_role: string): Promise<Activity[]> {
    try {
      // Get current user's session
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        logger.warn('No authenticated user found for activities');
        return [];
      }

      // Get user's workspace_id and role from users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('workspace_id, role')
        .eq('id', session.user.id)
        .single();

      if (userError) {
        logger.error('Error fetching user data', userError);
        return [];
      }

      // ADMIN users don't have workspace-specific data
      if (userData?.role === 'ADMIN' || !userData?.workspace_id) {
        return [];
      }

      // Query activity_log table with user info join
      const { data, error } = await supabase
        .from('activity_log')
        .select(`
          id,
          actor_id,
          action,
          entity_type,
          entity_id,
          title,
          metadata,
          created_at
        `)
        .eq('workspace_id', userData.workspace_id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        logger.error('Error fetching recent activities', error);
        return [];
      }

      if (!data || data.length === 0) {
        return [];
      }

      // Get actor names for all unique actor_ids
      const actorIds = [...new Set(data.map((row) => row.actor_id))];
      const { data: actorsData } = await supabase
        .from('users')
        .select('id, full_name')
        .in('id', actorIds);

      const actorNameMap = new Map(
        actorsData?.map((actor) => [actor.id, actor.full_name] as const) || []
      );

      // Map to Activity entities
      return data.map((row) => Activity.create({
        id: String(row.id),
        title: row.title,
        time: this.formatRelativeTime(row.created_at),
        type: this.mapActionToLegacyType(row.action),
        actorId: row.actor_id,
        actorName: actorNameMap.get(row.actor_id),
        entityType: row.entity_type,
        entityId: row.entity_id,
        metadata: row.metadata,
        createdAt: new Date(row.created_at),
      }));
    } catch (error) {
      logger.error('Error in getRecentActivities', error);
      return [];
    }
  }

  async getRecentActivitiesPaginated(_role: string, offset: number, limit: number): Promise<Activity[]> {
    try {
      // Get current user's session
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        logger.warn('No authenticated user found for activities');
        return [];
      }

      // Get user's workspace_id and role from users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('workspace_id, role')
        .eq('id', session.user.id)
        .single();

      if (userError) {
        logger.error('Error fetching user data', userError);
        return [];
      }

      // ADMIN users don't have workspace-specific data
      if (userData?.role === 'ADMIN' || !userData?.workspace_id) {
        return [];
      }

      // Query activity_log table with pagination
      const { data, error } = await supabase
        .from('activity_log')
        .select(`
          id,
          actor_id,
          action,
          entity_type,
          entity_id,
          title,
          metadata,
          created_at
        `)
        .eq('workspace_id', userData.workspace_id)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        logger.error('Error fetching paginated activities', error);
        return [];
      }

      if (!data || data.length === 0) {
        return [];
      }

      // Get actor names for all unique actor_ids
      const actorIds = [...new Set(data.map((row) => row.actor_id))];
      const { data: actorsData } = await supabase
        .from('users')
        .select('id, full_name')
        .in('id', actorIds);

      const actorNameMap = new Map(
        actorsData?.map((actor) => [actor.id, actor.full_name] as const) || []
      );

      // Map to Activity entities
      return data.map((row) => Activity.create({
        id: String(row.id),
        title: row.title,
        time: this.formatRelativeTime(row.created_at),
        type: this.mapActionToLegacyType(row.action),
        actorId: row.actor_id,
        actorName: actorNameMap.get(row.actor_id),
        entityType: row.entity_type,
        entityId: row.entity_id,
        metadata: row.metadata,
        createdAt: new Date(row.created_at),
      }));
    } catch (error) {
      logger.error('Error in getRecentActivitiesPaginated', error);
      return [];
    }
  }

  /**
   * Maps activity_log actions to legacy ActivityType for UI icon display
   */
  private mapActionToLegacyType(action: string): ActivityType {
    const mapping: Record<string, ActivityType> = {
      enrolled: 'enrollment',
      completed_course: 'completion',
      submitted: 'submission',
      graded: 'grade',
      quiz_completed: 'completion',
      lesson_completed: 'completion',
      post_created: 'question',
      reply_created: 'question',
      course_created: 'course',
      evaluation_created: 'course',
    };
    return mapping[action] || 'course';
  }

  async getUpcomingEvents(_role: string): Promise<ScheduleEvent[]> {
    try {
      // Get current user's session
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        logger.warn('No authenticated user found for upcoming events');
        return [];
      }

      // Get current day of week (convert JS 0=Sunday to our 0=Monday format)
      const today = new Date().getDay();
      const dayOfWeek = today === 0 ? 6 : today - 1;

      // Query ClassSchedules for today's classes
      const { data, error } = await supabase
        .from('ClassSchedules')
        .select(`
          id,
          start_hour,
          start_minute,
          duration_minutes,
          course:Courses!inner (
            subject:Subjects (
              name
            )
          )
        `)
        .eq('is_active', true)
        .eq('day_of_week', dayOfWeek)
        .order('start_hour')
        .limit(5);

      if (error) {
        logger.error('Error fetching upcoming events', error);
        return [];
      }

      if (!data || data.length === 0) {
        return [];
      }

      // Predefined colors for events
      const colors = ['purple', 'blue', 'green', 'orange', 'pink'];

      // Map to ScheduleEvent entities
      return (data as unknown as ScheduleWithCourseResult[]).map((schedule, index: number) => {
        const startTime = `${String(schedule.start_hour).padStart(2, '0')}:${String(schedule.start_minute).padStart(2, '0')}`;
        const endHour = schedule.start_hour + Math.floor((schedule.start_minute + schedule.duration_minutes) / 60);
        const endMinute = (schedule.start_minute + schedule.duration_minutes) % 60;
        const endTime = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;

        return new ScheduleEvent({
          id: String(schedule.id),
          title: schedule.course?.subject?.name || 'Sin título',
          time: `${startTime} - ${endTime}`,
          color: colors[index % colors.length],
        });
      });
    } catch (error) {
      logger.error('Error in getUpcomingEvents', error);
      return [];
    }
  }

  async getNotifications(_role: string): Promise<Notification[]> {
    try {
      // Get current user's session to get workspace_id
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        logger.warn('No authenticated user found for notifications');
        return [];
      }

      // Get user's workspace_id and role from users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('workspace_id, role')
        .eq('id', session.user.id)
        .single();

      if (userError) {
        logger.error('Error fetching user data', userError);
        return [];
      }

      // Try to use the view with read status first
      let notifications: Partial<NotificationViewRow>[] = [];
      let error: { message: string; details?: string; hint?: string; code?: string } | null = null;

      // ADMIN users get ADMIN-scoped notifications (not workspace-specific)
      if (userData?.role === 'ADMIN') {
        const adminResult = await supabase
          .from('notifications_with_read_status')
          .select('id, title, message_body, created_at, scope_type, is_read, read_at')
          .eq('scope_type', 'ADMIN')
          .order('created_at', { ascending: false })
          .limit(10);

        if (adminResult.error) {
          // Fallback to push_notifications table
          const fallbackResult = await supabase
            .from('push_notifications')
            .select('id, title, message_body, created_at, scope_type')
            .eq('scope_type', 'ADMIN')
            .order('created_at', { ascending: false })
            .limit(10);

          notifications = fallbackResult.data || [];
          error = fallbackResult.error;
        } else {
          notifications = adminResult.data || [];
        }
      } else if (!userData?.workspace_id) {
        // Non-admin users without workspace_id get no notifications
        return [];
      } else {
        // Regular users get workspace-specific notifications
        // Filter: workspace-wide notifications (GLOBAL, LEVEL, COURSE) OR user-specific (USER scope for this user)
        // Note: USER scope uses scope_reference_uuid (UUID type), others use scope_reference_id (bigint)
        const viewResult = await supabase
          .from('notifications_with_read_status')
          .select('id, title, message_body, created_at, scope_type, scope_reference_id, scope_reference_uuid, is_read, read_at')
          .eq('workspace_id', userData.workspace_id)
          .or(`scope_type.neq.USER,scope_reference_uuid.eq.${session.user.id}`)
          .order('created_at', { ascending: false })
          .limit(20);

        if (viewResult.error) {
          // Fallback to push_notifications table if view doesn't exist
          console.warn('View not available, falling back to push_notifications table:', viewResult.error.message);
          const fallbackResult = await supabase
            .from('push_notifications')
            .select('id, title, message_body, created_at, scope_type, scope_reference_id, scope_reference_uuid')
            .eq('workspace_id', userData.workspace_id)
            .or(`scope_type.neq.USER,scope_reference_uuid.eq.${session.user.id}`)
            .order('created_at', { ascending: false })
            .limit(20);

          notifications = fallbackResult.data || [];
          error = fallbackResult.error;
        } else {
          notifications = viewResult.data || [];
        }
      }

      if (error) {
        logger.error('Error fetching notifications', error);
        return [];
      }

      if (!notifications || notifications.length === 0) {
        return [];
      }

      // Map Supabase data to Notification entities with read status
      return notifications.map((notif) => {
        const message = notif.title
          ? `${notif.title}: ${notif.message_body}`
          : notif.message_body;

        return new Notification({
          id: String(notif.id),
          message: message || 'Notificación',
          time: this.formatRelativeTime(notif.created_at ?? new Date().toISOString()),
          isRead: notif.is_read ?? false,
          readAt: notif.read_at ?? null,
          scopeType: notif.scope_type ?? undefined,
        });
      });
    } catch (error) {
      logger.error('Error in getNotifications', error);
      return [];
    }
  }

  /**
   * Format timestamp to relative time (e.g., "Hace 5 min")
   */
  private formatRelativeTime(timestamp: string): string {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now.getTime() - past.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
      return 'Justo ahora';
    } else if (diffMinutes < 60) {
      return `Hace ${diffMinutes} min`;
    } else if (diffHours < 24) {
      return `Hace ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
    } else if (diffDays < 7) {
      return `Hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`;
    } else {
      return past.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short'
      });
    }
  }

  /**
   * Get courses completion progress for student
   * Returns: { completed, total, percentage }
   *
   * IMPORTANT: Counts courses as "completed" if they have 100% lesson progress
   * OR if enrollment status is COMPLETED (for courses without lessons).
   * This aligns with the individual course card display logic.
   */
  async getCoursesProgress(): Promise<{ completed: number; total: number; percentage: number }> {
    try {
      // Get current user's session
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        return { completed: 0, total: 0, percentage: 0 };
      }

      const userId = session.user.id;

      // Get all enrollments for this student with course info
      const { data: enrollments, error } = await supabase
        .from('Enrollments')
        .select('id, progress_status, course_id')
        .eq('student_id', userId);

      if (error) {
        logger.error('Error fetching courses progress', error);
        return { completed: 0, total: 0, percentage: 0 };
      }

      if (!enrollments || enrollments.length === 0) {
        return { completed: 0, total: 0, percentage: 0 };
      }

      // Get unique courses (in case of multiple enrollments to same course)
      const uniqueCourses = new Map<number | null, typeof enrollments[number]>();
      enrollments.forEach((enrollment) => {
        if (!uniqueCourses.has(enrollment.course_id)) {
          uniqueCourses.set(enrollment.course_id, enrollment);
        }
      });

      const total = uniqueCourses.size;

      // Batch fetch progress for all unique courses in a single RPC call
      const courseIds = Array.from(uniqueCourses.keys()).filter((id): id is number => id !== null);
      const progressMap = await this.getBatchCourseProgress([userId], courseIds);

      // Calculate completed count using batch results + HYBRID LOGIC
      let completed = 0;
      for (const enrollment of uniqueCourses.values()) {
        let progress = progressMap.get(`${userId}:${enrollment.course_id}`) ?? 0;

        // Apply HYBRID LOGIC: if progress is 0% and status is COMPLETED, treat as 100%
        if (progress === 0 && enrollment.progress_status === 'COMPLETED') {
          progress = 100;
        }

        // Count as completed if progress is 100%
        if (progress === 100) {
          completed++;
        }
      }

      const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

      return { completed, total, percentage };
    } catch (error) {
      logger.error('Error in getCoursesProgress', error);
      return { completed: 0, total: 0, percentage: 0 };
    }
  }

  /**
   * Get student streak (consecutive days completing lessons)
   * Returns: { currentStreak, longestStreak }
   */
  async getStudentStreak(): Promise<{ currentStreak: number; longestStreak: number }> {
    try {
      // Get current user's session
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        return { currentStreak: 0, longestStreak: 0 };
      }

      const userId = session.user.id;

      // Call the SQL function to calculate streak
      const { data, error } = await supabase.rpc('get_student_streak', {
        p_student_id: userId
      });

      if (error) {
        logger.error('Error fetching student streak', error);
        return { currentStreak: 0, longestStreak: 0 };
      }

      if (!data || data.length === 0) {
        return { currentStreak: 0, longestStreak: 0 };
      }

      // The function returns { current_streak, longest_streak }
      return {
        currentStreak: data[0]?.current_streak || 0,
        longestStreak: data[0]?.longest_streak || 0
      };
    } catch (error) {
      logger.error('Error in getStudentStreak', error);
      return { currentStreak: 0, longestStreak: 0 };
    }
  }

  /**
   * Mark a notification as read for the current user
   */
  async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        logger.warn('No authenticated user found for marking notification as read');
        return;
      }

      console.log('markNotificationAsRead: Attempting to mark notification', notificationId, 'as read for user', session.user.id);

      // Try using the RPC function first
      const { data: rpcResult, error: rpcError } = await supabase.rpc('mark_notification_as_read', {
        p_notification_id: parseInt(notificationId, 10)
      });

      if (rpcError) {
        // Fallback: Insert directly into notification_reads table (lowercase)
        console.warn('RPC not available, falling back to direct insert:', rpcError.message);
        const { error } = await supabase
          .from('notification_reads')
          .upsert({
            notification_id: parseInt(notificationId, 10),
            user_id: session.user.id,
            read_at: new Date().toISOString()
          }, {
            onConflict: 'notification_id,user_id'
          });

        if (error) {
          console.error('Error marking notification as read (fallback):', error);
        } else {
          console.log('markNotificationAsRead: Successfully marked via fallback');
        }
      } else {
        // Log RPC result for debugging
        console.log('markNotificationAsRead: RPC result:', rpcResult);
        if (rpcResult && !rpcResult.success) {
          console.error('markNotificationAsRead: RPC returned error:', rpcResult.error);
        }
      }
    } catch (error) {
      logger.error('Error in markNotificationAsRead', error);
    }
  }

  /**
   * Mark all notifications as read for the current user
   */
  async markAllNotificationsAsRead(): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        logger.warn('No authenticated user found for marking all notifications as read');
        return;
      }

      // Get user's workspace_id
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('workspace_id')
        .eq('id', session.user.id)
        .single();

      if (userError || !userData?.workspace_id) {
        logger.error('Error fetching user workspace', userError);
        return;
      }

      console.log('markAllNotificationsAsRead: Marking all notifications as read for workspace', userData.workspace_id);

      // Try using the RPC function first
      const { data: rpcResult, error: rpcError } = await supabase.rpc('mark_all_notifications_as_read', {
        p_workspace_id: userData.workspace_id
      });

      if (rpcError) {
        // Fallback: Get all notifications and insert reads
        console.warn('RPC not available, falling back to manual insert:', rpcError.message);

        const { data: notifications, error: fetchError } = await supabase
          .from('push_notifications')
          .select('id')
          .eq('workspace_id', userData.workspace_id);

        if (fetchError || !notifications) {
          console.error('Error fetching notifications:', fetchError);
          return;
        }

        // Insert read records for all notifications
        const readRecords = notifications.map(n => ({
          notification_id: n.id,
          user_id: session.user.id,
          read_at: new Date().toISOString()
        }));

        if (readRecords.length > 0) {
          const { error } = await supabase
            .from('notification_reads')
            .upsert(readRecords, {
              onConflict: 'notification_id,user_id'
            });

          if (error) {
            console.error('Error marking all notifications as read (fallback):', error);
          } else {
            console.log('markAllNotificationsAsRead: Successfully marked', readRecords.length, 'via fallback');
          }
        }
      } else {
        // Log RPC result for debugging
        console.log('markAllNotificationsAsRead: RPC result:', rpcResult);
        if (rpcResult && !rpcResult.success) {
          console.error('markAllNotificationsAsRead: RPC returned error:', rpcResult.error);
        }
      }
    } catch (error) {
      logger.error('Error in markAllNotificationsAsRead', error);
    }
  }

  // ============================================
  // Welcome Videos
  // ============================================

  /**
   * Get welcome videos applicable to current user for current cycle
   */
  async getWelcomeVideosForUser(
    userRole: string,
    currentCycleId: number | null
  ): Promise<WelcomeVideo[]> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        logger.warn('No authenticated user found for welcome videos');
        return [];
      }

      // Get user's workspace_id
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('workspace_id')
        .eq('id', session.user.id)
        .single();

      if (userError || !userData?.workspace_id) {
        logger.error('Error fetching user workspace', userError);
        return [];
      }

      // Query welcome_videos_with_view_status view
      let query = supabase
        .from('welcome_videos_with_view_status')
        .select('*')
        .eq('workspace_id', userData.workspace_id)
        .eq('is_active', true)
        .or(`target_role.eq.${userRole},target_role.eq.all`)
        .order('display_order', { ascending: true });

      // Filter by cycle if specified
      if (currentCycleId) {
        query = query.or(`cycle_id.eq.${currentCycleId},cycle_id.is.null`);
      }

      const { data, error } = await query;

      if (error) {
        // Fallback to direct table query if view doesn't exist
        console.warn('View not available, falling back to direct query:', error.message);
        return this.getWelcomeVideosFallback(userData.workspace_id, userRole, currentCycleId);
      }

      if (!data || data.length === 0) {
        return [];
      }

      // Log detailed info for debugging
      console.log('getWelcomeVideosForUser: Fetched', data.length, 'videos');
      data.forEach((row) => {
        console.log(`  - Video ${row.id} "${row.title}": is_viewed=${row.is_viewed}, show_once=${row.show_once}, viewed_at=${row.viewed_at}`);
      });

      // Filter videos that should be displayed
      const now = new Date();
      const filteredVideos = data
        .filter((row) => {
          // Check scheduling
          if (row.scheduled_at && new Date(row.scheduled_at) > now) return false;
          if (row.expires_at && new Date(row.expires_at) <= now) return false;
          // Check show_once logic
          if (row.show_once && row.is_viewed) return false;
          return true;
        })
        .map((row) => WelcomeVideoMapper.fromDatabase(row));

      console.log('getWelcomeVideosForUser: After filtering, showing', filteredVideos.length, 'videos');
      return filteredVideos;
    } catch (error) {
      logger.error('Error in getWelcomeVideosForUser', error);
      return [];
    }
  }

  /**
   * Fallback method when view doesn't exist
   */
  private async getWelcomeVideosFallback(
    workspaceId: number,
    userRole: string,
    currentCycleId: number | null
  ): Promise<WelcomeVideo[]> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return [];

    // Get videos
    let query = supabase
      .from('welcome_videos')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('is_active', true)
      .or(`target_role.eq.${userRole},target_role.eq.all`)
      .order('display_order', { ascending: true });

    if (currentCycleId) {
      query = query.or(`cycle_id.eq.${currentCycleId},cycle_id.is.null`);
    }

    const { data: videos, error: videoError } = await query;

    if (videoError || !videos) {
      logger.error('Error fetching welcome videos', videoError);
      return [];
    }

    // Get view status for these videos
    const videoIds = videos.map((v) => v.id);
    const { data: views } = await supabase
      .from('welcome_video_views')
      .select('welcome_video_id, viewed_at, completed')
      .eq('user_id', session.user.id)
      .in('welcome_video_id', videoIds);

    type ViewInfo = Pick<WelcomeVideoViewRow, 'welcome_video_id' | 'viewed_at' | 'completed'>;
    const viewMap = new Map<WelcomeVideoViewRow['welcome_video_id'], ViewInfo>(
      views?.map((v) => [v.welcome_video_id, v]) || []
    );

    const now = new Date();
    return videos
      .filter((row) => {
        if (row.scheduled_at && new Date(row.scheduled_at) > now) return false;
        if (row.expires_at && new Date(row.expires_at) <= now) return false;
        if (row.show_once && viewMap.has(row.id)) return false;
        return true;
      })
      .map((row) => {
        const view = viewMap.get(row.id);
        return WelcomeVideoMapper.fromDatabase({
          ...row,
          is_viewed: !!view,
          viewed_at: view?.viewed_at,
          completed: view?.completed,
        });
      });
  }

  /**
   * Get all welcome videos (for admin management)
   */
  async getAllWelcomeVideos(): Promise<WelcomeVideo[]> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        logger.warn('No authenticated user found');
        return [];
      }

      // Get user's workspace_id
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('workspace_id')
        .eq('id', session.user.id)
        .single();

      if (userError || !userData?.workspace_id) {
        logger.error('Error fetching user workspace', userError);
        return [];
      }

      // Get all videos with cycle names
      const { data, error } = await supabase
        .from('welcome_videos')
        .select(`
          *,
          cycle:AcademicCycles (
            name
          )
        `)
        .eq('workspace_id', userData.workspace_id)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error fetching all welcome videos', error);
        return [];
      }

      if (!data) return [];

      return data.map((row) => WelcomeVideoMapper.fromDatabase({
        ...row,
        cycle_name: (row.cycle as { name: string | null } | null)?.name || null,
      }));
    } catch (error) {
      logger.error('Error in getAllWelcomeVideos', error);
      return [];
    }
  }

  /**
   * Get a single welcome video by ID
   */
  async getWelcomeVideoById(videoId: number): Promise<WelcomeVideo | null> {
    try {
      const { data, error } = await supabase
        .from('welcome_videos')
        .select(`
          *,
          cycle:AcademicCycles (
            name
          )
        `)
        .eq('id', videoId)
        .single();

      if (error) {
        logger.error('Error fetching welcome video', error);
        return null;
      }

      if (!data) return null;

      return WelcomeVideoMapper.fromDatabase({
        ...data,
        cycle_name: data.cycle?.name || null,
      });
    } catch (error) {
      logger.error('Error in getWelcomeVideoById', error);
      return null;
    }
  }

  /**
   * Create a new welcome video
   */
  async createWelcomeVideo(input: CreateWelcomeVideoInput): Promise<WelcomeVideo> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.user) {
      throw new Error('No authenticated user');
    }

    // Get user's workspace_id
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('workspace_id')
      .eq('id', session.user.id)
      .single();

    if (userError || !userData?.workspace_id) {
      throw new Error('Could not fetch user workspace');
    }

    const payload = {
      ...WelcomeVideoMapper.toCreatePayload(input),
      workspace_id: userData.workspace_id,
      created_by: session.user.id,
    };

    const { data, error } = await supabase
      .from('welcome_videos')
      .insert(payload)
      .select()
      .single();

    if (error) {
      logger.error('Error creating welcome video', error);
      throw new Error(`Failed to create welcome video: ${error.message}`);
    }

    return WelcomeVideoMapper.fromDatabase(data);
  }

  /**
   * Update an existing welcome video
   */
  async updateWelcomeVideo(
    videoId: number,
    input: UpdateWelcomeVideoInput
  ): Promise<WelcomeVideo> {
    const updatePayload: Record<string, unknown> = {};

    if (input.cycleId !== undefined) updatePayload.cycle_id = input.cycleId;
    if (input.targetRole !== undefined) updatePayload.target_role = input.targetRole;
    if (input.videoType !== undefined) updatePayload.video_type = input.videoType;
    if (input.videoUrl !== undefined) updatePayload.video_url = input.videoUrl;
    if (input.title !== undefined) updatePayload.title = input.title;
    if (input.description !== undefined) updatePayload.description = input.description;
    if (input.thumbnailUrl !== undefined) updatePayload.thumbnail_url = input.thumbnailUrl;
    if (input.displayOrder !== undefined) updatePayload.display_order = input.displayOrder;
    if (input.isActive !== undefined) updatePayload.is_active = input.isActive;
    if (input.showOnce !== undefined) updatePayload.show_once = input.showOnce;
    if (input.scheduledAt !== undefined) updatePayload.scheduled_at = input.scheduledAt;
    if (input.expiresAt !== undefined) updatePayload.expires_at = input.expiresAt;

    const { data, error } = await supabase
      .from('welcome_videos')
      .update(updatePayload)
      .eq('id', videoId)
      .select()
      .single();

    if (error) {
      logger.error('Error updating welcome video', error);
      throw new Error(`Failed to update welcome video: ${error.message}`);
    }

    return WelcomeVideoMapper.fromDatabase(data);
  }

  /**
   * Delete a welcome video
   */
  async deleteWelcomeVideo(videoId: number): Promise<void> {
    const { error } = await supabase
      .from('welcome_videos')
      .delete()
      .eq('id', videoId);

    if (error) {
      logger.error('Error deleting welcome video', error);
      throw new Error(`Failed to delete welcome video: ${error.message}`);
    }
  }

  /**
   * Mark a welcome video as viewed by current user
   */
  async markWelcomeVideoAsViewed(
    videoId: number,
    completed: boolean = false,
    duration: number = 0
  ): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        logger.warn('No authenticated user found');
        return;
      }

      console.log('markWelcomeVideoAsViewed: Marking video', videoId, 'as viewed for user', session.user.id, 'completed:', completed);

      // Try using the RPC function first
      const { data: rpcResult, error: rpcError } = await supabase.rpc('mark_welcome_video_viewed', {
        p_video_id: videoId,
        p_completed: completed,
        p_duration: duration,
      });

      if (rpcError) {
        // Fallback: Insert/update directly
        console.warn('RPC not available, falling back to direct upsert:', rpcError.message);
        const { error } = await supabase
          .from('welcome_video_views')
          .upsert({
            welcome_video_id: videoId,
            user_id: session.user.id,
            viewed_at: new Date().toISOString(),
            completed,
            watch_duration_seconds: duration,
          }, {
            onConflict: 'welcome_video_id,user_id'
          });

        if (error) {
          console.error('Error marking welcome video as viewed (fallback):', error);
        } else {
          console.log('markWelcomeVideoAsViewed: Successfully marked via fallback');
        }
      } else {
        // Log RPC result for debugging
        console.log('markWelcomeVideoAsViewed: RPC result:', rpcResult);
        if (rpcResult && !rpcResult.success) {
          console.error('markWelcomeVideoAsViewed: RPC returned error:', rpcResult.error);
        }
      }
    } catch (error) {
      logger.error('Error in markWelcomeVideoAsViewed', error);
    }
  }

  // ==========================================
  // Pending Videos (Student Dashboard)
  // ==========================================

  /**
   * Get courses with pending (unwatched) video lessons for a student
   * This query fetches courses where the student is enrolled and has video lessons
   * that haven't been completed yet.
   */
  async getPendingVideosForStudent(
    studentId: string,
    limit: number = 10
  ): Promise<PendingVideoRow[]> {
    try {
      // Query to get courses with video lessons and their completion status
      // We need to:
      // 1. Get enrollments for the student
      // 2. Get courses with video lessons
      // 3. Count total videos and watched videos per course
      // 4. Find the next unwatched lesson

      const { data: enrollments, error: enrollmentError } = await supabase
        .from('Enrollments')
        .select(`
          id,
          course:Courses (
            id,
            teacher:users!Courses_teacher_id_fkey (
              id,
              full_name
            ),
            subject:Subjects (
              id,
              name
            )
          )
        `)
        .eq('student_id', studentId)
        .eq('progress_status', 'ENROLLED');

      if (enrollmentError) {
        console.error('[PendingVideos] Error fetching enrollments:', enrollmentError);
        return [];
      }

      console.log(`[PendingVideos] Found ${enrollments?.length || 0} enrollments for student ${studentId}`);

      if (!enrollments || enrollments.length === 0) {
        console.log('[PendingVideos] No enrollments found');
        return [];
      }

      // For each course, get video lesson stats
      const pendingVideosPromises = (enrollments as unknown as PendingEnrollmentResult[]).map(async (enrollment) => {
        const course = enrollment.course;
        if (!course) return null;

        // Get all video lessons for this course
        const { data: lessons, error: lessonsError } = await supabase
          .from('Lessons')
          .select('id, title, content_type, content_body')
          .eq('course_id', course.id)
          .eq('content_type', 'VIDEO')
          .order('sort_order', { ascending: true });

        if (lessonsError) {
          console.error('Error fetching lessons for course', course.id, ':', lessonsError);
          return null;
        }

        if (!lessons || lessons.length === 0) {
          // No video lessons in this course
          return null;
        }

        console.log(`[PendingVideos] Course ${course.id} has ${lessons.length} video lessons`);

        // Get lesson progress for this student
        const lessonIds = (lessons as LessonSelectResult[]).map((l) => l.id);
        const { data: progress, error: progressError } = await supabase
          .from('LessonProgress')
          .select('lesson_id, completed, completed_at')
          .eq('student_id', studentId)
          .in('lesson_id', lessonIds);

        if (progressError) {
          console.error('Error fetching lesson progress:', progressError);
        }

        // Calculate stats
        const progressMap = new Map(
          (progress || []).map((p: LessonProgressSelectResult) => [p.lesson_id, p])
        );

        const totalVideos = lessons.length;
        let watchedVideos = 0;
        let lastWatchedAt: string | null = null;
        let nextLesson: LessonSelectResult | null = null;

        for (const lesson of lessons) {
          const lessonProgress = progressMap.get(lesson.id);
          if (lessonProgress?.completed) {
            watchedVideos++;
            if (!lastWatchedAt || (lessonProgress.completed_at && lessonProgress.completed_at > lastWatchedAt)) {
              lastWatchedAt = lessonProgress.completed_at;
            }
          } else if (!nextLesson) {
            // First unwatched lesson is the next one
            nextLesson = lesson;
          }
        }

        // Skip courses with all videos watched
        if (watchedVideos >= totalVideos) {
          return null;
        }

        return {
          course_id: course.id,
          course_name: course.subject?.name || 'Curso sin nombre',
          subject_name: course.subject?.name || 'Sin materia',
          subject_id: course.subject?.id || 0,
          instructor_name: course.teacher?.full_name || 'Sin profesor',
          instructor_id: course.teacher?.id || '',
          total_videos: totalVideos,
          watched_videos: watchedVideos,
          last_watched_at: lastWatchedAt,
          next_lesson_id: nextLesson?.id || null,
          next_lesson_title: nextLesson?.title || null,
          thumbnail_url: undefined, // Lessons table doesn't have thumbnail_url
        } as PendingVideoRow;
      });

      const results = await Promise.all(pendingVideosPromises);

      // Filter out null results and limit
      const pendingVideos = results
        .filter((r): r is PendingVideoRow => r !== null)
        .sort((a, b) => {
          // Sort by urgency: courses not watched recently first
          if (!a.last_watched_at && !b.last_watched_at) return 0;
          if (!a.last_watched_at) return -1;
          if (!b.last_watched_at) return 1;
          return new Date(b.last_watched_at).getTime() - new Date(a.last_watched_at).getTime();
        })
        .slice(0, limit);

      console.log(`[PendingVideos] Returning ${pendingVideos.length} courses with pending videos`);
      return pendingVideos;
    } catch (error) {
      console.error('[PendingVideos] Error in getPendingVideosForStudent:', error);
      return [];
    }
  }

  // ==========================================
  // Teacher Dashboard Methods
  // ==========================================

  /**
   * Get statistics for teacher dashboard
   */
  async getTeacherStats(teacherId: string): Promise<TeacherStatsDTO> {
    try {
      // Get pending submissions count
      // First, get teacher's courses
      const { data: teacherCoursesData } = await supabase
        .from('Courses')
        .select('id')
        .eq('teacher_id', teacherId);

      const teacherCourseIds = teacherCoursesData?.map(c => c.id) || [];

      let pendingCount = 0;
      if (teacherCourseIds.length > 0) {
        // Then get evaluations for those courses
        const { data: teacherEvaluations } = await supabase
          .from('Evaluations')
          .select('id')
          .in('course_id', teacherCourseIds);

        const teacherEvaluationIds = teacherEvaluations?.map(e => e.id) || [];

        if (teacherEvaluationIds.length > 0) {
          const { count } = await supabase
            .from('Submissions')
            .select('*', { count: 'exact', head: true })
            .is('grade', null)
            .eq('status', 'SUBMITTED')
            .in('evaluation_id', teacherEvaluationIds);
          pendingCount = count || 0;
        }
      }

      // Get active courses count for current cycle
      const { data: userData } = await supabase
        .from('users')
        .select('workspace_id')
        .eq('id', teacherId)
        .single();

      // Get current active cycle
      const { data: activeCycle } = await supabase
        .from('AcademicCycles')
        .select('id')
        .eq('workspace_id', userData?.workspace_id || 0)
        .eq('status', true)
        .single();

      // Get courses count - filter by cycle only if an active cycle exists
      let coursesQuery = supabase
        .from('Courses')
        .select('*', { count: 'exact', head: true })
        .eq('teacher_id', teacherId);

      // Only filter by cycle if we have an active cycle
      if (activeCycle?.id) {
        coursesQuery = coursesQuery.eq('academic_cycle_id', activeCycle.id);
      }

      const { count: coursesCount } = await coursesQuery;

      // Get total unique students across all teacher's courses
      const { data: enrollments } = await supabase
        .from('Enrollments')
        .select('student_id, course:Courses!inner(teacher_id)')
        .eq('course.teacher_id', teacherId)
        .eq('progress_status', 'ENROLLED');

      const uniqueStudents = new Set((enrollments as EnrollmentWithCourseTeacherId[] | null)?.map((e) => e.student_id) || []);

      // Calculate average progress across all courses
      const courses = await this.getTeacherCourses(teacherId, 100);
      const avgProgress = courses.length > 0
        ? Math.round(courses.reduce((sum, c) => sum + c.progressPercentage, 0) / courses.length)
        : 0;

      return {
        pendingSubmissions: pendingCount || 0,
        activeCourses: coursesCount || 0,
        totalStudents: uniqueStudents.size,
        averageProgress: avgProgress,
      };
    } catch (error) {
      logger.error('Error in getTeacherStats', error);
      return {
        pendingSubmissions: 0,
        activeCourses: 0,
        totalStudents: 0,
        averageProgress: 0,
      };
    }
  }

  /**
   * Get pending submissions for a teacher (ungraded work)
   * Includes both essay submissions AND quiz attempts with open-text questions pending grading
   */
  async getTeacherPendingSubmissions(
    teacherId: string,
    limit: number = 10
  ): Promise<PendingSubmissionDTO[]> {
    try {
      const pendingItems: PendingSubmissionDTO[] = [];

      // 1. Query essay submissions (existing logic)
      const { data: essayData, error: essayError } = await supabase
        .from('Submissions')
        .select(`
          id,
          student_id,
          submitted_at,
          status,
          student:users!Submissions_student_id_fkey (
            id,
            full_name
          ),
          evaluation:Evaluations!inner (
            id,
            title,
            type,
            due_date,
            course:Courses!inner (
              id,
              teacher_id,
              subject:Subjects (
                name
              )
            )
          )
        `)
        .eq('evaluation.course.teacher_id', teacherId)
        .is('grade', null)
        .eq('status', 'SUBMITTED')
        .order('submitted_at', { ascending: true })
        .limit(limit);

      if (essayError) {
        logger.error('Error fetching pending essay submissions', essayError);
      }

      // Map essay submissions
      if (essayData && essayData.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const row of essayData as any[]) {
          const dueDate = row.evaluation?.due_date ? new Date(row.evaluation.due_date) : null;
          const submittedAt = new Date(row.submitted_at);
          const isLate = dueDate ? submittedAt > dueDate : false;

          let priority: 'high' | 'medium' | 'low' = 'low';
          if (isLate) {
            priority = 'high';
          } else {
            const now = new Date();
            const daysOld = Math.floor((now.getTime() - submittedAt.getTime()) / (1000 * 60 * 60 * 24));
            if (daysOld >= 3) priority = 'high';
            else if (daysOld >= 1) priority = 'medium';
          }

          pendingItems.push({
            id: `essay-${row.id}`,
            studentId: row.student_id,
            studentName: row.student?.full_name || 'Estudiante',
            studentAvatar: undefined,
            courseId: String(row.evaluation?.course?.id || ''),
            courseName: row.evaluation?.course?.subject?.name || 'Curso',
            evaluationId: String(row.evaluation?.id || ''),
            evaluationTitle: row.evaluation?.title || 'Evaluación',
            evaluationType: 'ESSAY',
            submittedAt,
            dueDate,
            isLate,
            priority,
          });
        }
      }

      // 2. Query quiz attempts with pending open-text questions
      // First, get teacher's courses
      const { data: teacherCourses } = await supabase
        .from('Courses')
        .select('id')
        .eq('teacher_id', teacherId);

      console.log('Teacher courses:', teacherCourses);

      if (teacherCourses && teacherCourses.length > 0) {
        const courseIds = teacherCourses.map(c => c.id);

        // Get evaluations for these courses
        const { data: evaluations } = await supabase
          .from('Evaluations')
          .select('id, title, due_date, course_id, course:Courses(subject:Subjects(name))')
          .in('course_id', courseIds);

        console.log('Teacher evaluations:', evaluations);

        if (evaluations && evaluations.length > 0) {
          const evaluationIds = evaluations.map(e => e.id);

          // Get completed attempts for these evaluations
          const { data: quizData, error: quizError } = await supabase
            .from('AssessmentAttempts')
            .select(`
              id,
              student_id,
              evaluation_id,
              completed_at,
              status,
              student:users!AssessmentAttempts_student_id_fkey (
                id,
                full_name
              )
            `)
            .in('evaluation_id', evaluationIds)
            .eq('status', 'COMPLETED')
            .order('completed_at', { ascending: true })
            .limit(limit * 2);

          console.log('Quiz attempts:', quizData, 'Error:', quizError);

          if (quizError) {
            logger.error('Error fetching quiz attempts', quizError);
          }

          // For each quiz attempt, check if it has ungraded open-text responses
          if (quizData && quizData.length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            for (const attempt of quizData as any[]) {
              // Check if this attempt has any ungraded open-text questions
              const { data: ungradedResponses } = await supabase
                .from('QuestionResponses')
                .select(`
                  id,
                  is_correct,
                  question:Questions (
                    question_type
                  )
                `)
                .eq('attempt_id', attempt.id)
                .is('is_correct', null);

              console.log(`Attempt ${attempt.id} ungraded responses:`, ungradedResponses);

              // Filter to only OPEN_TEXT questions
              const openTextUngraded = (ungradedResponses as QuestionResponseWithQuestion[] | null)?.filter(
                (r) => r.question?.question_type === 'OPEN_TEXT'
              ) || [];

              // If there are ungraded open-text responses, add to pending
              if (openTextUngraded.length > 0) {
                const evaluation = evaluations.find(e => e.id === attempt.evaluation_id);
                const completedAt = attempt.completed_at ? new Date(attempt.completed_at) : new Date();
                const dueDate = evaluation?.due_date ? new Date(evaluation.due_date) : null;
                const isLate = dueDate ? completedAt > dueDate : false;

                let priority: 'high' | 'medium' | 'low' = 'low';
                if (isLate) {
                  priority = 'high';
                } else {
                  const now = new Date();
                  const daysOld = Math.floor((now.getTime() - completedAt.getTime()) / (1000 * 60 * 60 * 24));
                  if (daysOld >= 3) priority = 'high';
                  else if (daysOld >= 1) priority = 'medium';
                }

                pendingItems.push({
                  id: `quiz-${attempt.id}`,
                  studentId: attempt.student_id,
                  studentName: attempt.student?.full_name || 'Estudiante',
                  studentAvatar: undefined,
                  courseId: String(evaluation?.course_id || ''),
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  courseName: (evaluation?.course as any)?.subject?.name || 'Curso',
                  evaluationId: String(evaluation?.id || ''),
                  evaluationTitle: `${evaluation?.title || 'Quiz'} (${openTextUngraded.length} pregunta${openTextUngraded.length > 1 ? 's' : ''} abierta${openTextUngraded.length > 1 ? 's' : ''})`,
                  evaluationType: 'QUIZ',
                  submittedAt: completedAt,
                  dueDate,
                  isLate,
                  priority,
                });
              }
            }
          }
        }
      }

      // Sort by submission date and limit
      pendingItems.sort((a, b) => a.submittedAt.getTime() - b.submittedAt.getTime());
      return pendingItems.slice(0, limit);

    } catch (error) {
      logger.error('Error in getTeacherPendingSubmissions', error);
      return [];
    }
  }

  /**
   * Get courses taught by a teacher with metrics
   */
  async getTeacherCourses(
    teacherId: string,
    limit: number = 4
  ): Promise<TeacherCourseDTO[]> {
    try {
      // Get user's workspace to filter by current cycle
      const { data: userData } = await supabase
        .from('users')
        .select('workspace_id')
        .eq('id', teacherId)
        .single();

      // Get current active cycle
      const { data: activeCycle } = await supabase
        .from('AcademicCycles')
        .select('id')
        .eq('workspace_id', userData?.workspace_id || 0)
        .eq('status', true)
        .single();

      // Get courses - filter by cycle only if an active cycle exists
      let coursesQuery = supabase
        .from('Courses')
        .select(`
          id,
          modality,
          subject:Subjects!inner (
            id,
            name,
            level:Levels (
              id,
              name
            )
          )
        `)
        .eq('teacher_id', teacherId);

      // Only filter by cycle if we have an active cycle
      if (activeCycle?.id) {
        coursesQuery = coursesQuery.eq('academic_cycle_id', activeCycle.id);
      }

      const { data: courses, error } = await coursesQuery.limit(limit);

      if (error) {
        logger.error('Error fetching teacher courses', error);
        return [];
      }

      if (!courses || courses.length === 0) {
        return [];
      }

      // Predefined colors
      const colors = ['#6D5DFE', '#FF6B9D', '#4ECDC4', '#FFA500', '#9D8DFE', '#FF8C94'];

      // Process each course to get metrics
      const coursesWithMetrics = await Promise.all(
        (courses as unknown as TeacherCourseSelectResult[]).map(async (course, index: number) => {
          // Get enrolled students count
          const { count: studentsCount } = await supabase
            .from('Enrollments')
            .select('*', { count: 'exact', head: true })
            .eq('course_id', course.id)
            .eq('progress_status', 'ENROLLED');

          // Get average grade
          const { data: grades } = await supabase
            .from('Enrollments')
            .select('final_grade')
            .eq('course_id', course.id)
            .not('final_grade', 'is', null);

          const averageGrade = grades && grades.length > 0
            ? grades.reduce((sum: number, e: Pick<EnrollmentRow, 'final_grade'>) => sum + (e.final_grade || 0), 0) / grades.length
            : null;

          // Get progress percentage (completed evaluations / total evaluations)
          const { count: totalEvaluations } = await supabase
            .from('Evaluations')
            .select('*', { count: 'exact', head: true })
            .eq('course_id', course.id);

          const { count: completedEvaluations } = await supabase
            .from('Evaluations')
            .select('*', { count: 'exact', head: true })
            .eq('course_id', course.id)
            .lte('due_date', new Date().toISOString());

          const progressPercentage = totalEvaluations && totalEvaluations > 0
            ? Math.round((completedEvaluations || 0) / totalEvaluations * 100)
            : 0;

          // Get pending grades count
          // First get evaluation IDs for this course
          const { data: courseEvaluations } = await supabase
            .from('Evaluations')
            .select('id')
            .eq('course_id', course.id);

          const evaluationIds = courseEvaluations?.map(e => e.id) || [];

          let pendingGradesCount = 0;
          if (evaluationIds.length > 0) {
            const { count } = await supabase
              .from('Submissions')
              .select('*', { count: 'exact', head: true })
              .is('grade', null)
              .eq('status', 'SUBMITTED')
              .in('evaluation_id', evaluationIds);
            pendingGradesCount = count || 0;
          }

          // Get next class from schedule
          const today = new Date().getDay();
          const dayOfWeek = today === 0 ? 6 : today - 1;

          const { data: nextSchedule } = await supabase
            .from('ClassSchedules')
            .select('day_of_week, start_hour, start_minute, duration_minutes, location')
            .eq('course_id', course.id)
            .eq('is_active', true)
            .gte('day_of_week', dayOfWeek)
            .order('day_of_week')
            .order('start_hour')
            .limit(1)
            .single();

          let nextClass = null;
          if (nextSchedule) {
            const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
            const startTime = `${String(nextSchedule.start_hour).padStart(2, '0')}:${String(nextSchedule.start_minute).padStart(2, '0')}`;
            const endHour = nextSchedule.start_hour + Math.floor((nextSchedule.start_minute + nextSchedule.duration_minutes) / 60);
            const endMinute = (nextSchedule.start_minute + nextSchedule.duration_minutes) % 60;
            const endTime = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;

            nextClass = {
              dayOfWeek: dayNames[nextSchedule.day_of_week],
              startTime,
              endTime,
              isToday: nextSchedule.day_of_week === dayOfWeek,
            };
          }

          return {
            courseId: String(course.id),
            courseName: course.subject?.name || 'Curso',
            subjectName: course.subject?.name || 'Materia',
            levelName: course.subject?.level?.name || 'Nivel',
            color: colors[index % colors.length],
            studentsEnrolled: studentsCount || 0,
            averageGrade: averageGrade ? Math.round(averageGrade * 10) / 10 : null,
            progressPercentage,
            nextClass,
            modality: course.modality?.toUpperCase() === 'ONLINE' ? 'ONLINE' : 'ONSITE',
            pendingGrades: pendingGradesCount || 0,
          } as TeacherCourseDTO;
        })
      );

      return coursesWithMetrics;
    } catch (error) {
      logger.error('Error in getTeacherCourses', error);
      return [];
    }
  }

  /**
   * Get upcoming classes for a teacher
   */
  async getTeacherUpcomingClasses(
    teacherId: string,
    limit: number = 5
  ): Promise<UpcomingClassDTO[]> {
    try {
      const today = new Date().getDay();
      const dayOfWeek = today === 0 ? 6 : today - 1;

      const { data, error } = await supabase
        .from('ClassSchedules')
        .select(`
          id,
          day_of_week,
          start_hour,
          start_minute,
          duration_minutes,
          location,
          course:Courses!inner (
            id,
            modality,
            teacher_id,
            subject:Subjects (
              name
            )
          )
        `)
        .eq('course.teacher_id', teacherId)
        .eq('is_active', true)
        .gte('day_of_week', dayOfWeek)
        .order('day_of_week')
        .order('start_hour')
        .limit(limit);

      if (error) {
        logger.error('Error fetching upcoming classes', error);
        return [];
      }

      if (!data || data.length === 0) {
        return [];
      }

      const dayNames = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

      return (data as unknown as TeacherScheduleSelectResult[]).map((schedule) => {
        const startTime = `${String(schedule.start_hour).padStart(2, '0')}:${String(schedule.start_minute).padStart(2, '0')}`;
        const endHour = schedule.start_hour + Math.floor((schedule.start_minute + schedule.duration_minutes) / 60);
        const endMinute = (schedule.start_minute + schedule.duration_minutes) % 60;
        const endTime = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;

        return {
          id: String(schedule.id),
          courseName: schedule.course?.subject?.name || 'Curso',
          subjectName: schedule.course?.subject?.name || 'Materia',
          dayOfWeek: dayNames[schedule.day_of_week],
          startTime,
          endTime,
          isToday: schedule.day_of_week === dayOfWeek,
          modality: schedule.course?.modality?.toUpperCase() === 'ONLINE' ? 'ONLINE' : 'ONSITE',
          location: schedule.location,
        } as UpcomingClassDTO;
      });
    } catch (error) {
      logger.error('Error in getTeacherUpcomingClasses', error);
      return [];
    }
  }
}
